import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, inr } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useSEO } from "@/hooks/useSEO";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ShoppingBag, Zap, Truck, RotateCcw, Shield } from "lucide-react";

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { addToCart } = useApp();
  const [p, setP] = useState(null);
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [qty, setQty] = useState(1);

  useSEO({
    title: p ? `${p.name} · Buy Online` : "Loading Product",
    description: p ? `${p.description || p.name} — Shop at Venvay. ${p.category} ${p.subcategory}. Free shipping over ₹1499.` : "Loading product on Venvay.",
    keywords: p ? `${p.name}, ${p.category} ${p.subcategory}, venvay ${p.subcategory}, buy ${p.subcategory} online india` : "",
    canonicalPath: `/product/${id}`,
    image: p?.images?.[0],
    type: "product",
  });

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => {
      setP(r.data);
      setSize((r.data.sizes || [])[0] || "");
      setColor((r.data.colors || [])[0] || "");
    });
  }, [id]);

  if (!p) return <div className="max-w-6xl mx-auto p-8 text-center text-muted-foreground">Loading…</div>;

  const off = p.original_price ? Math.round(((p.original_price - p.price) / p.original_price) * 100) : 0;

  const add = () => {
    if (!size) return toast.error("Please select a size");
    addToCart(p, size, qty, color);
    toast.success("Added to bag", { description: `${p.name} · Size ${size}` });
  };
  const buyNow = () => { add(); nav("/cart"); };

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <div className="grid md:grid-cols-2 gap-12">
        <div>
          <div className="aspect-[3/4] bg-[#2A231D] rounded-md overflow-hidden border border-[#D4B25C]/10">
            <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" />
          </div>
        </div>
        <div className="md:sticky md:top-32 md:self-start">
          <p className="label-tag">{p.category} · {p.subcategory}</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black mt-3 leading-none">{p.name}</h1>
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": p.name,
            "image": p.images,
            "description": p.description,
            "brand": { "@type": "Brand", "name": "Venvay" },
            "offers": {
              "@type": "Offer",
              "priceCurrency": "INR",
              "price": p.price,
              "availability": (p.stock || 0) > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
              "url": `https://style-venvay.emergent.host/product/${p.id}`
            }
          })}} />
          <div className="flex items-baseline gap-3 mt-6">
            <span className="text-3xl font-bold">{inr(p.price)}</span>
            {p.original_price && (
              <>
                <span className="text-lg text-muted-foreground line-through">{inr(p.original_price)}</span>
                <span className="text-xs font-bold text-[#8B2E2E] tracking-widest uppercase">{off}% off</span>
              </>
            )}
          </div>
          <p className="text-xs text-[#8B2E2E] mt-1 tracking-widest uppercase font-semibold">Inclusive of taxes</p>

          <p className="mt-6 text-muted-foreground leading-relaxed">{p.description}</p>

          {p.sizes?.length > 0 && (
            <div className="mt-8">
              <div className="flex items-center justify-between mb-3">
                <p className="label-tag">Select Size</p>
                <span className="text-xs text-muted-foreground">{p.stock} in stock</span>
              </div>
              <div className="flex gap-2 flex-wrap">
                {p.sizes.map((s) => (
                  <button
                    key={s}
                    data-testid={`size-${s}`}
                    onClick={() => setSize(s)}
                    className={`h-11 min-w-11 px-4 border font-semibold text-sm transition-colors ${size === s ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-black/20 hover:border-[#8B2E2E]"}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {p.colors?.length > 0 && (
            <div className="mt-6">
              <p className="label-tag mb-3">Color</p>
              <div className="flex gap-2 flex-wrap">
                {p.colors.map((c) => (
                  <button key={c} onClick={() => setColor(c)} data-testid={`color-${c}`}
                    className={`px-4 py-2 border text-sm ${color === c ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-black/20 hover:border-[#8B2E2E]"}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6 flex items-center gap-4">
            <p className="label-tag">Qty</p>
            <div className="flex items-center border border-black/20">
              <button data-testid="qty-dec" onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-10 hover:bg-black/5">−</button>
              <span data-testid="qty-value" className="w-10 text-center font-semibold">{qty}</span>
              <button data-testid="qty-inc" onClick={() => setQty(qty + 1)} className="w-10 h-10 hover:bg-black/5">+</button>
            </div>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Button data-testid="add-to-cart-btn" onClick={add} size="lg" variant="outline" className="flex-1 border-black hover:bg-black hover:text-white h-12 uppercase tracking-widest text-xs">
              <ShoppingBag size={16} className="mr-2" /> Add to Bag
            </Button>
            <Button data-testid="buy-now-btn" onClick={buyNow} size="lg" className="flex-1 bg-[#8B2E2E] hover:bg-[#6f2323] h-12 uppercase tracking-widest text-xs">
              <Zap size={16} className="mr-2" /> Buy Now
            </Button>
          </div>

          <div className="mt-8 pt-6 border-t border-black/10 grid grid-cols-3 gap-3 text-xs text-center">
            <div className="flex flex-col items-center gap-1"><Truck size={16} className="text-[#8B2E2E]" /><span>Free shipping ₹1499+</span></div>
            <div className="flex flex-col items-center gap-1"><RotateCcw size={16} className="text-[#8B2E2E]" /><span>7-day easy returns</span></div>
            <div className="flex flex-col items-center gap-1"><Shield size={16} className="text-[#8B2E2E]" /><span>Secure checkout</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}
